import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  MerchantOnboardingPrefill,
  MerchantOnboardingResult,
  MerchantOnboardingStatusResult,
  RegenerateMerchantOnboardingSessionRequest,
  StartMerchantOnboardingRequest,
} from '../models';

interface MerchantOnboardingState {
  prefill: MerchantOnboardingPrefill | null;
  status: MerchantOnboardingStatusResult | null;
  /** Consumed once by the page to open the hosted URL, then cleared. */
  startResult: MerchantOnboardingResult | null;
  loadingPrefill: boolean;
  loadingStatus: boolean;
  submitting: boolean;
  /** The lead itself expired: only a brand new registration can recover it. */
  requiresNewRegistration: boolean;
  error: string | null;
}

const initialState: MerchantOnboardingState = {
  prefill: null,
  status: null,
  startResult: null,
  loadingPrefill: false,
  loadingStatus: false,
  submitting: false,
  requiresNewRegistration: false,
  error: null,
};

const merchantOnboardingSlice = createSlice({
  name: 'merchantOnboarding',
  initialState,
  reducers: {
    fetchPrefillStart: (state) => {
      state.loadingPrefill = true;
      state.error = null;
    },
    fetchPrefillSuccess: (state, action: PayloadAction<MerchantOnboardingPrefill>) => {
      state.loadingPrefill = false;
      state.prefill = action.payload;
      state.error = null;
    },
    fetchPrefillFailed: (state, action: PayloadAction<string>) => {
      state.loadingPrefill = false;
      state.error = action.payload;
    },

    // `silent` keeps background refreshes from flashing the spinner over a rendered screen.
    fetchStatusStart: (state, action: PayloadAction<{ silent?: boolean } | undefined>) => {
      state.loadingStatus = !action.payload?.silent;
      state.error = null;
    },
    fetchStatusSuccess: (state, action: PayloadAction<MerchantOnboardingStatusResult>) => {
      state.loadingStatus = false;
      state.status = action.payload;
      state.error = null;
    },
    fetchStatusFailed: (state, action: PayloadAction<string>) => {
      state.loadingStatus = false;
      state.error = action.payload;
    },

    startOnboardingStart: (state, action: PayloadAction<StartMerchantOnboardingRequest>) => {
      state.submitting = true;
      state.error = null;
    },
    startOnboardingSuccess: (state, action: PayloadAction<MerchantOnboardingResult>) => {
      state.submitting = false;
      state.startResult = action.payload;
      state.requiresNewRegistration = false;
      state.error = null;
    },
    startOnboardingFailed: (state, action: PayloadAction<string>) => {
      state.submitting = false;
      state.error = action.payload;
    },

    regenerateSessionStart: (
      state,
      action: PayloadAction<RegenerateMerchantOnboardingSessionRequest>
    ) => {
      state.submitting = true;
      state.error = null;
    },

    requireNewRegistration: (state) => {
      state.submitting = false;
      state.requiresNewRegistration = true;
    },

    clearStartResult: (state) => {
      state.startResult = null;
    },
  },
});

export const merchantOnboardingActions = merchantOnboardingSlice.actions;
export const merchantOnboardingReducer = merchantOnboardingSlice.reducer;

// Selectors
export const selectMerchantOnboardingPrefill = (state: any) => state.merchantOnboarding.prefill;
export const selectMerchantOnboardingStatus = (state: any) => state.merchantOnboarding.status;
export const selectMerchantOnboardingStartResult = (state: any) =>
  state.merchantOnboarding.startResult;
export const selectMerchantOnboardingLoadingPrefill = (state: any) =>
  state.merchantOnboarding.loadingPrefill;
export const selectMerchantOnboardingLoadingStatus = (state: any) =>
  state.merchantOnboarding.loadingStatus;
export const selectMerchantOnboardingSubmitting = (state: any) =>
  state.merchantOnboarding.submitting;
export const selectMerchantOnboardingRequiresNewRegistration = (state: any) =>
  state.merchantOnboarding.requiresNewRegistration;
export const selectMerchantOnboardingError = (state: any) => state.merchantOnboarding.error;
